import React, { useEffect, useMemo, useState } from 'react';
import { useAuth, apiClient } from '../auth/AuthContext';

interface RawBmsZoneEntry {
  id: string;
  buildingId: string;
  floorId?: string;
  name?: string;
  bmsZones?: Array<string | { S?: string; N?: string }>;
  createdAt?: string;
  updatedAt?: string;
}

interface NormalizedZone {
  id: string;
  buildingId: string;
  floorId: string;
  name: string;
  bmsZones: string[];
  createdAt?: string;
  updatedAt?: string;
}

interface ZoneFormState {
  name: string;
  floorId: string;
  bmsZonesText: string;
}

const defaultFormState: ZoneFormState = {
  name: '',
  floorId: '',
  bmsZonesText: '',
};

const normalizeBmsZones = (value: RawBmsZoneEntry['bmsZones']): string[] => {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      if (entry && typeof entry === 'object') {
        if (typeof entry.S === 'string') return entry.S;
        if (typeof entry.N === 'string') return entry.N;
      }
      return '';
    })
    .map((zone) => zone.trim())
    .filter(Boolean);
};

const normalizeZones = (items: RawBmsZoneEntry[]): NormalizedZone[] =>
  items.map((item) => ({
    id: item.id,
    buildingId: item.buildingId,
    floorId: item.floorId ?? '',
    name: item.name ?? item.id,
    bmsZones: normalizeBmsZones(item.bmsZones),
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  }));

const BmsZonesPage: React.FC = () => {
  const { user, token } = useAuth();
  const [zones, setZones] = useState<NormalizedZone[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingEnabled, setEditingEnabled] = useState(false);
  const [activeZone, setActiveZone] = useState<NormalizedZone | null>(null);
  const [formState, setFormState] = useState<ZoneFormState>(defaultFormState);
  const [saving, setSaving] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  const buildingId = user?.buildingId || '';
  const canLoad = Boolean(token && buildingId);

  const sortedZones = useMemo(
    () =>
      [...zones].sort((a, b) => {
        if (a.floorId === b.floorId) {
          return a.name.localeCompare(b.name);
        }
        return a.floorId.localeCompare(b.floorId);
      }),
    [zones],
  );

  useEffect(() => {
    if (!canLoad) return;

    const fetchZones = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await apiClient.get<RawBmsZoneEntry[]>(`/zones?buildingId=${encodeURIComponent(buildingId)}`);
        setZones(normalizeZones(response));
      } catch (err: any) {
        setError(err.message || 'Failed to load zones');
      } finally {
        setLoading(false);
      }
    };

    fetchZones();
  }, [buildingId, canLoad, refreshToken]);

  const resetForm = () => {
    setActiveZone(null);
    setFormState(defaultFormState);
  };

  const handleEdit = (zone: NormalizedZone) => {
    setActiveZone(zone);
    setFormState({
      name: zone.name,
      floorId: zone.floorId,
      bmsZonesText: zone.bmsZones.join(', '),
    });
    setEditingEnabled(true);
  };

  const handleDelete = async (zone: NormalizedZone) => {
    if (!editingEnabled) return;
    const confirmed = window.confirm(`Delete "${zone.name}"? This cannot be undone.`);
    if (!confirmed) return;

    try {
      setSaving(true);
      await apiClient.delete(`/zones/${encodeURIComponent(zone.id)}?buildingId=${encodeURIComponent(buildingId)}`);
      setRefreshToken((prev) => prev + 1);
    } catch (err: any) {
      setError(err.message || 'Failed to delete zone');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editingEnabled || !buildingId) return;

    if (!formState.name.trim() || !formState.floorId.trim()) {
      setError('Name and floor fields are required.');
      return;
    }

    const zoneList = formState.bmsZonesText
      .split(/[,\\n]/)
      .map((entry) => entry.trim())
      .filter(Boolean);

    const payload = {
      buildingId,
      name: formState.name.trim(),
      floorId: formState.floorId.trim(),
      bmsZones: zoneList,
    };

    try {
      setSaving(true);
      setError('');

      if (activeZone) {
        await apiClient.put(`/zones/${encodeURIComponent(activeZone.id)}`, payload);
      } else {
        await apiClient.post('/zones', payload);
      }

      resetForm();
      setRefreshToken((prev) => prev + 1);
    } catch (err: any) {
      setError(err.message || 'Failed to save zone');
    } finally {
      setSaving(false);
    }
  };

  if (!buildingId) {
    return (
      <div className="ah-shell">
        <div className="ah-panel">
          <div className="ah-card">
            <h1 className="ah-title">BMS Zones</h1>
            <p className="muted">Sign in with a building-assigned account to manage zones.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ah-shell">
      <div className="ah-panel">
        <div className="ah-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
            <div>
              <h1 className="ah-title" style={{ marginBottom: 4 }}>
                BMS Zones
              </h1>
              <p className="muted">Viewing entries for building {buildingId}</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                className="ah-btn ah-btn-outline"
                onClick={() => setRefreshToken((prev) => prev + 1)}
                disabled={loading || saving}
              >
                Refresh
              </button>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={editingEnabled}
                  onChange={() => {
                    if (editingEnabled) resetForm();
                    setEditingEnabled((prev) => !prev);
                  }}
                />
                Enable editing
              </label>
            </div>
          </div>

          {error && (
            <div
              style={{
                backgroundColor: '#fed7d7',
                border: '1px solid #feb2b2',
                color: '#742a2a',
                borderRadius: 8,
                padding: 12,
                marginBottom: 24,
              }}
            >
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: 32 }}>Loading zones…</div>
          ) : (
            <div className="ah-table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Area Name</th>
                    <th>Floor</th>
                    <th>BMS Zones</th>
                    <th>Updated</th>
                    {editingEnabled && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {sortedZones.length === 0 ? (
                    <tr>
                      <td colSpan={editingEnabled ? 5 : 4} style={{ textAlign: 'center', padding: 24 }}>
                        No BMS zones found for this building.
                      </td>
                    </tr>
                  ) : (
                    sortedZones.map((zone) => (
                      <tr key={zone.id}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{zone.name}</div>
                          <div className="muted" style={{ fontSize: 12 }}>
                            {zone.id}
                          </div>
                        </td>
                        <td>{zone.floorId || '—'}</td>
                        <td>{zone.bmsZones.length ? zone.bmsZones.join(', ') : '—'}</td>
                        <td>{zone.updatedAt ? new Date(zone.updatedAt).toLocaleString() : '—'}</td>
                        {editingEnabled && (
                          <td>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button className="ah-btn ah-btn-outline" onClick={() => handleEdit(zone)} disabled={saving}>
                                Edit
                              </button>
                              <button
                                className="ah-btn"
                                style={{ background: '#e53e3e' }}
                                onClick={() => handleDelete(zone)}
                                disabled={saving}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {editingEnabled && (
            <div className="ah-card" style={{ marginTop: 32, background: 'var(--surface-hover)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <h3 style={{ margin: 0 }}>{activeZone ? 'Edit BMS Zone' : 'Create BMS Zone'}</h3>
                  <p className="muted" style={{ margin: 0 }}>
                    Provide a comma-separated list of BMS identifiers.
                  </p>
                </div>
                {activeZone && (
                  <button className="ah-btn ah-btn-outline" onClick={resetForm} disabled={saving}>
                    Cancel edit
                  </button>
                )}
              </div>
              <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 16 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Area Name</label>
                    <input
                      type="text"
                      className="ah-input"
                      value={formState.name}
                      onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g. Lobby East"
                      required
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Floor ID</label>
                    <input
                      type="text"
                      className="ah-input"
                      value={formState.floorId}
                      onChange={(e) => setFormState((prev) => ({ ...prev, floorId: e.target.value }))}
                      placeholder="e.g. floor-01"
                      required
                    />
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>BMS Zones</label>
                  <textarea
                    className="ah-input"
                    rows={3}
                    value={formState.bmsZonesText}
                    onChange={(e) => setFormState((prev) => ({ ...prev, bmsZonesText: e.target.value }))}
                    placeholder='Example: "0, 1, 2" or "A1, A2"'
                  />
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button className="ah-btn" type="submit" disabled={saving}>
                    {activeZone ? 'Save changes' : 'Create zone'}
                  </button>
                  <button type="button" className="ah-btn ah-btn-outline" onClick={resetForm} disabled={saving}>
                    Clear
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BmsZonesPage;

